import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { periodLabel } from "@/lib/pricing";
import { fmtPlainCVE } from "@/components/PaymentLogos";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DownloadSimple, ArrowsClockwise, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

export const V4_STATUS = {
  succeeded: ["Pago", "border-emerald-200 bg-emerald-50 text-emerald-700"],
  pending: ["Pendente", "border-amber-200 bg-amber-50 text-amber-700"],
  pending_verification: ["A verificar", "border-amber-200 bg-amber-50 text-amber-700"],
  failed: ["Recusado", "border-red-200 bg-red-50 text-red-700"],
  cancelled: ["Cancelado", "border-slate-200 bg-slate-50 text-slate-600"],
};

export async function downloadReceipt(p) {
  try {
    const { data } = await api.get(`/vinti4/payments/${p.id}/receipt`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = `Recibo_${p.merchant_ref}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch (err) { toast.error(apiError(err)); }
}

export function V4StatusBadge({ status, testid }) {
  const [label, cls] = V4_STATUS[status] || V4_STATUS.pending;
  return <Badge variant="outline" className={cls} data-testid={testid}>{label}</Badge>;
}

function Row({ p, onCheck, busy }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 py-3 last:border-0" data-testid={`v4-payment-${p.id}`}>
      <div className="text-sm">
        <div className="font-medium">Plano {p.plan_name} · {periodLabel(p.months)} · {fmtPlainCVE(p.amount_cve)}</div>
        <div className="text-xs text-muted-foreground">Ref. <span className="font-mono">{p.merchant_ref}</span> · {fmtDate(p.created_at)}{p.receipt_number ? ` · ${p.receipt_number}` : ""}</div>
      </div>
      <div className="flex items-center gap-2">
        <V4StatusBadge status={p.status} testid={`v4-status-${p.id}`} />
        {p.status === "succeeded" ? (
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadReceipt(p)} data-testid={`v4-receipt-${p.id}`}><DownloadSimple size={15} className="mr-1" /> Recibo</Button>
        ) : ["pending", "pending_verification"].includes(p.status) && (
          <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => onCheck(p)} data-testid={`v4-check-${p.id}`}>
            {busy ? <CircleNotch className="animate-spin" /> : <><ArrowsClockwise size={15} className="mr-1" /> Verificar estado</>}
          </Button>
        )}
      </div>
    </div>
  );
}

export function Vinti4Payments({ onChange }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => { api.get("/vinti4/payments").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  if (!items.length) return null;
  const check = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/vinti4/payments/${p.id}/check-status`);
      toast.success(`Estado SISP: ${data.sisp?.transactionStatusDescription || data.payment.status}`);
      load(); onChange?.();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };
  return (
    <section className="rounded-xl border border-border bg-card p-5" data-testid="vinti4-payments">
      <h2 className="mb-2 font-display text-lg font-bold">Pagamentos com Cartão Vinti4 / Visa / Mastercard / Amex</h2>
      {items.map((p) => <Row key={p.id} p={p} onCheck={check} busy={busy === p.id} />)}
    </section>
  );
}
